import React from "react";
import {
  BenefitSection,
  BenefitCard,
} from "@/.storyblok/types/319751/storyblok-components";
import { storyblokEditable, SbBlokData } from "@storyblok/react";
import Link from "next/link";
import Image from "next/image";
import { renderRichText } from "@/lib/richText";
import checkMarkIcon from '@/assets/images/partners/check-mark.svg';

interface BenefitSectionProps {
  blok: BenefitSection & SbBlokData
}

type BenefitWithSbData = BenefitCard & SbBlokData;

const BenefitSectionComponent: React.FC<BenefitSectionProps> = ({ blok }) => {
  if (!blok) return null;

  const defaultBenefits = blok.benefits?.filter((benefit) => benefit.type !== "red_icon") || [];
  const redIconBenefits = blok.benefits?.filter((benefit) => benefit.type === "red_icon") || [];

  if (blok.benefit_type === "blue_checkmark") {
    return (
      <div className="highlight-section-light">
        <div className="container">
          <div className="padding-medium">
            <div className="full-width text-center">
              {blok.title && (
                <h2
                  className={`highlight-text ${blok.title_color ? "text-" + blok.title_color : "text-primary"}`}
                >
                  {blok.title}
                </h2>
              )}
              {blok.description && (
                <p className="mb-3">{blok.description}</p>
              )}
            </div>
            <div className="full-width margin-bottom-medium">
              <div className="partner-points-row">
                <div className="one-half">
                  <ul className="list-unstyled">
                    {blok.benefits?.slice(0, Math.ceil(blok.benefits.length / 2)).map((benefit) => (
                      <li key={benefit._uid} className="partner-point" {...storyblokEditable(benefit as BenefitWithSbData)}>
                        <div className="partner-point-icon">
                          <Image
                            alt={benefit.title || ""}
                            title={benefit.title || ""}
                            src={checkMarkIcon}
                            width={21}
                            height={18}
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            loading="lazy"
                            quality={75}
                          />
                        </div>
                        <div className="partner-point-content">
                          <h4>{benefit.title}</h4>
                          {benefit.description && (
                            <div dangerouslySetInnerHTML={{ __html: renderRichText(benefit.description) }} />
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="one-half">
                  <ul className="list-unstyled">
                    {blok.benefits?.slice(Math.ceil(blok.benefits.length / 2)).map((benefit) => (
                      <li key={benefit._uid} className="partner-point" {...storyblokEditable(benefit as BenefitWithSbData)}>
                        <div className="partner-point-icon">
                          <Image
                            alt={benefit.title || ""}
                            title={benefit.title || ""}
                            src={checkMarkIcon}
                            width={21}
                            height={18}
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            loading="lazy"
                            quality={75}
                          />
                        </div>
                        <div className="partner-point-content">
                          <h4>{benefit.title}</h4>
                          {benefit.description && (
                            <div dangerouslySetInnerHTML={{ __html: renderRichText(benefit.description) }} />
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...storyblokEditable(blok)}>
      <section className="py-4 py-md-5 my-2 my-md-3">
        <div className="container">
          {blok.title && (
            <h2
              className={`lh-base text-center mb-5 pb-3 ${blok.title_color ? "text-" + blok.title_color : "text-primary"}`}
            >
              {blok.title}
            </h2>
          )}
          {blok.description && (
            <p className="lead col-12 mx-auto text-center mb-5">
              {blok.description}
            </p>
          )}

          {defaultBenefits.length > 0 && (
            <ul className="row list-unstyled g-4 scroll-trigger bulletproof-checklist on">
              {defaultBenefits.map((benefit) => (
                <li
                  key={benefit._uid}
                  className="col-24 col-md-12 col-lg-8"
                  {...storyblokEditable(benefit as BenefitWithSbData)}
                >
                  <div className="card card-lighter card-green-check justify-content-center p-3 h-100">
                    <h3 className="h5 mb-1">{benefit.title}</h3>
                    {benefit.description && (
                      <div
                        className="mb-o"
                        dangerouslySetInnerHTML={{ __html: renderRichText(benefit.description) }}
                      />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {redIconBenefits.length > 0 && (
            <div className="row justify-content-center g-lg-4">
              {redIconBenefits.map((benefit) => (
                <div
                  key={benefit._uid}
                  className="col-24 col-lg-12 mb-3"
                  {...storyblokEditable(benefit as BenefitWithSbData)}
                >
                  <div className="card card-lighter h-100 p-4 text-center">
                    {benefit.icon?.filename && (
                      <Image
                        alt={benefit.icon.alt || ""}
                        className="img-fluid mb-2 mx-auto"
                        src={benefit.icon.filename}
                        width={120}
                        height={120}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    )}
                    <h3 className="mt-2">{benefit.title}</h3>
                    {benefit.description && (
                      <div
                        className="mb-o"
                        dangerouslySetInnerHTML={{ __html: renderRichText(benefit.description) }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

        {blok.cta_button && blok.cta_button_link && (
          <div className="text-center container mt-5">
            <Link
              href={blok.cta_button_link.url || "#"}
              className="button button-large button-primary js-cta-button"
            >
              {blok.cta_button}
            </Link>
          </div>
        )}
        </div>
      </section>
    </div>
  );
};

export default BenefitSectionComponent;